import { useEventListener } from '@library/hooks'
import { usePersistence } from '@library/model'
import { assert, isOfType } from 'common/helpers'
import { useEffect, useSyncExternalStore } from 'react'

export const themes: Theme[] = ['dark', 'light']
const isTheme = isOfType(themes)

export type Theme = 'dark' | 'light'

class ThemeStore {
  private theme: Theme
  private subscriptions = new Set<VoidFunction>()

  public constructor(private storage: Storage) {
    this.theme = this.updateTheme()
  }

  public setStorage = (storage: Storage) => {
    this.storage = storage
    this.updateTheme()
  }

  public updateTheme = () => {
    const themeFromMatch = window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light'
    const themeFromStorage = this.storage.getItem('geomate_theme')
    const result = themeFromStorage ?? themeFromMatch

    assert(
      isTheme(result),
      `No valid theme found (${result}): from storage ${themeFromStorage}, from match ${themeFromMatch}`,
    )

    this.theme = result
    this.notify()

    return result
  }

  setTheme = (value: Theme) => {
    this.theme = value
    this.storage.setItem('geomate_theme', value)

    this.notify()
  }

  public getSnapshot = () => {
    return this.theme
  }

  public subscribe = (cb: VoidFunction) => {
    this.subscriptions.add(cb)

    return () => {
      this.subscriptions.delete(cb)
    }
  }

  private notify = () => {
    this.subscriptions.forEach(callback => {
      callback()
    })
  }
}

const themeProvider = new ThemeStore(window.localStorage)

export const useGeomateTheme = (): [Theme, ThemeStore['setTheme']] => {
  const storage = usePersistence()

  useEffect(() => {
    themeProvider.setStorage(storage)
  }, [storage])

  useEventListener('storage', () => {
    themeProvider.updateTheme()
  })

  return [
    useSyncExternalStore(themeProvider.subscribe, themeProvider.getSnapshot),
    themeProvider.setTheme,
  ]
}
